import { useEffect, useRef, useState } from "react";
import { X, LogIn, UserPlus, Loader2, KeyRound, ArrowLeft, Mail, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, formatApiError } from "@/lib/auth";
import { requestPasswordReset, resetPasswordByPhone } from "@/lib/api";
import { sendOtp, resetRecaptcha } from "@/lib/firebase";

const INPUT_CLS =
  "bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full";
const LABEL_CLS = "overline block mb-1.5";

// One dialog with three modes:
//   1. Sign Up (tab)
//   2. Login (tab)
//   3. Forgot Password (opened from a link on the Login tab)
// Tab-switching preserves the email so users don't retype it after clicking
// "Already have an account?" mid-form.
export default function AuthDialog({ show, initialTab = "signup", onClose, onFirstSignup }) {
  const { signup, login } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [forgot, setForgot] = useState(false);        // true → show forgot form
  const [forgotSent, setForgotSent] = useState(false); // true → show success card
  const [forgotChannel, setForgotChannel] = useState("email"); // "email" | "phone"
  const [phone, setPhone] = useState("+91");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState(null); // Firebase ConfirmationResult
  const [phoneStep, setPhoneStep] = useState("phone");  // "phone" → "otp" → "password" → "done"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const recaptchaRef = useRef(null);

  // Reset every time the dialog reopens from a specific entry point.
  useEffect(() => {
    if (show) {
      setTab(initialTab);
      setForgot(false);
      setForgotSent(false);
      setForgotChannel("email");
      setPhoneStep("phone");
      setConfirmation(null);
      setOtp("");
      setNewPassword("");
      setError("");
    } else {
      resetRecaptcha();
    }
  }, [show, initialTab]);

  if (!show) return null;

  const isSignup = tab === "signup" && !forgot;
  const isLogin  = tab === "login" && !forgot;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isSignup) {
        await signup({ email: email.trim(), password, name: name.trim() });
        toast.success("Account created — you're signed in.");
        onFirstSignup?.();
      } else if (isLogin) {
        await login({ email: email.trim(), password });
        toast.success("Welcome back!");
      } else if (forgot) {
        await requestPasswordReset(email.trim());
        setForgotSent(true);
      }
      if (!forgot) onClose?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Something went wrong.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur flex items-center justify-center p-4"
      data-testid="auth-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-md relative rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="auth-dialog-close"
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-900 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Tab switcher — hidden when the user is in the Forgot flow */}
        {!forgot && (
          <div className="flex border-b border-slate-200">
            <button
              type="button"
              data-testid="auth-tab-signup"
              onClick={() => { setTab("signup"); setError(""); }}
              className={
                "flex-1 py-3 mono text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2 " +
                (isSignup
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              <UserPlus className="w-4 h-4" /> Sign Up
            </button>
            <button
              type="button"
              data-testid="auth-tab-login"
              onClick={() => { setTab("login"); setError(""); }}
              className={
                "flex-1 py-3 mono text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2 " +
                (isLogin
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-500 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              <LogIn className="w-4 h-4" /> Login
            </button>
          </div>
        )}

        <div className="p-6">
          {forgot ? (
            <>
              <button
                type="button"
                data-testid="auth-forgot-back"
                onClick={() => { setForgot(false); setForgotSent(false); setError(""); }}
                className="mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-[#2563EB] inline-flex items-center gap-1 mb-3"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </button>
              <h2 className="heading text-2xl font-bold mb-1 flex items-center gap-2">
                <KeyRound className="w-6 h-6 text-[#2563EB]" /> Reset your password
              </h2>

              {/* Channel toggle: Email vs SMS OTP */}
              <div className="flex items-center gap-2 mt-3 mb-4 border-b border-slate-200">
                <button
                  type="button"
                  data-testid="auth-forgot-channel-email"
                  onClick={() => { setForgotChannel("email"); setForgotSent(false); setPhoneStep("phone"); setError(""); }}
                  className={
                    "flex items-center gap-1.5 mono text-xs uppercase tracking-widest pb-2 border-b-2 transition-colors " +
                    (forgotChannel === "email"
                      ? "border-[#2563EB] text-[#2563EB]"
                      : "border-transparent text-slate-500 hover:text-slate-900")
                  }
                >
                  <Mail className="w-3.5 h-3.5" /> Email
                </button>
                <button
                  type="button"
                  data-testid="auth-forgot-channel-phone"
                  onClick={() => { setForgotChannel("phone"); setForgotSent(false); setError(""); }}
                  className={
                    "flex items-center gap-1.5 mono text-xs uppercase tracking-widest pb-2 border-b-2 transition-colors " +
                    (forgotChannel === "phone"
                      ? "border-[#2563EB] text-[#2563EB]"
                      : "border-transparent text-slate-500 hover:text-slate-900")
                  }
                >
                  <Smartphone className="w-3.5 h-3.5" /> Mobile OTP
                </button>
              </div>

              {forgotChannel === "email" ? (
              forgotSent ? (
                <>
                  <div
                    data-testid="auth-forgot-sent"
                    className="mt-4 border border-[#2563EB]/30 bg-blue-50 text-slate-700 text-sm px-4 py-3 rounded-md"
                  >
                    <div className="font-semibold text-slate-900 mb-1">Check your inbox.</div>
                    We just sent a reset link to <span className="mono">{email}</span> (if an account exists).
                    The link is valid for 60 minutes.
                  </div>
                  <Button
                    type="button"
                    data-testid="auth-forgot-done"
                    onClick={() => { setForgot(false); setForgotSent(false); setTab("login"); }}
                    className="w-full mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-11 transition-colors"
                  >
                    Back to login
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-5">
                    Tell us the email on your account. We'll send you a link to pick a new password.
                  </p>
                  <form onSubmit={submit} className="space-y-4">
                    <div>
                      <label htmlFor="auth-forgot-email" className={LABEL_CLS}>Email</label>
                      <Input
                        id="auth-forgot-email"
                        data-testid="auth-forgot-email-input"
                        className={INPUT_CLS}
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                      />
                    </div>
                    {error && (
                      <div
                        data-testid="auth-error"
                        className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md"
                      >
                        {error}
                      </div>
                    )}
                    <Button
                      type="submit"
                      data-testid="auth-forgot-submit"
                      disabled={busy || !email}
                      className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? (
                        <span className="flex items-center gap-2 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                        </span>
                      ) : "Send reset link →"}
                    </Button>
                  </form>
                </>
              )
              ) : (
                // ---- PHONE OTP FLOW ----
                <PhoneResetFlow
                  phone={phone} setPhone={setPhone}
                  otp={otp} setOtp={setOtp}
                  newPassword={newPassword} setNewPassword={setNewPassword}
                  confirmation={confirmation} setConfirmation={setConfirmation}
                  phoneStep={phoneStep} setPhoneStep={setPhoneStep}
                  busy={busy} setBusy={setBusy}
                  error={error} setError={setError}
                  onDone={async ({ user }) => {
                    // Auth cookie was set by the backend — refresh /me and close.
                    toast.success(`Password updated — welcome back, ${user?.name || user?.email}`);
                    onClose?.();
                    window.location.reload();
                  }}
                  onBackToLogin={() => { setForgot(false); setForgotChannel("email"); setPhoneStep("phone"); setTab("login"); }}
                  recaptchaRef={recaptchaRef}
                />
              )}
            </>
          ) : (
          <>
          <h2 className="heading text-2xl font-bold mb-1">
            {isSignup ? "Create your account" : "Welcome back"}
          </h2>
          <p className="text-sm text-slate-500 mb-5">
            {isSignup
              ? "Sign up so your logo & company data stay saved across every device."
              : "Log in with your email + password."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <div>
                <label htmlFor="auth-name" className={LABEL_CLS}>Name <span className="text-slate-300">(optional)</span></label>
                <Input
                  id="auth-name"
                  data-testid="auth-name-input"
                  className={INPUT_CLS}
                  placeholder="Your name or company"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  autoComplete="name"
                />
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className={LABEL_CLS}>Email</label>
              <Input
                id="auth-email"
                data-testid="auth-email-input"
                className={INPUT_CLS}
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="auth-password" className={LABEL_CLS + " mb-0"}>Password</label>
                {isLogin && (
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => { setForgot(true); setError(""); }}
                    className="text-[11px] mono uppercase tracking-widest text-[#2563EB] hover:underline"
                  >
                    Forgot?
                  </button>
                )}
              </div>
              <Input
                id="auth-password"
                data-testid="auth-password-input"
                className={INPUT_CLS}
                type="password"
                placeholder={isSignup ? "At least 6 characters" : "Your password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isSignup ? 6 : undefined}
                autoComplete={isSignup ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={busy || !email || !password || (isSignup && password.length < 6)}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <span className="flex items-center gap-2 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Please wait…
                </span>
              ) : (
                isSignup ? "Sign up →" : "Log in →"
              )}
            </Button>
          </form>

          <div className="text-center mt-4 text-xs text-slate-500 mono">
            {isSignup ? (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-to-login"
                  onClick={() => { setTab("login"); setError(""); }}
                  className="text-[#2563EB] hover:underline"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                New here?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-to-signup"
                  onClick={() => { setTab("signup"); setError(""); }}
                  className="text-[#2563EB] hover:underline"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
          </>
          )}
        </div>
        {/* Invisible reCAPTCHA container — Firebase attaches an iframe here
            when we call signInWithPhoneNumber. Must be mounted BEFORE the
            call. Kept inside the dialog so it's removed on unmount. */}
        <div id="recaptcha-container" data-testid="recaptcha-container" />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Phone OTP → new password sub-flow, extracted for readability.
// -----------------------------------------------------------------------
function PhoneResetFlow({
  phone, setPhone,
  otp, setOtp,
  newPassword, setNewPassword,
  confirmation, setConfirmation,
  phoneStep, setPhoneStep,
  busy, setBusy,
  error, setError,
  onDone, onBackToLogin,
}) {
  const sendCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.startsWith("+") || phone.length < 8) {
      setError("Enter your number with country code, e.g. +919876543210");
      return;
    }
    setBusy(true);
    try {
      const conf = await sendOtp(phone.trim(), "recaptcha-container");
      setConfirmation(conf);
      setPhoneStep("otp");
    } catch (e) {
      setError(e?.message || "Could not send the OTP. Double-check the number.");
      resetRecaptcha();
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!otp || otp.length < 4) { setError("Enter the 6-digit code you got by SMS."); return; }
    setBusy(true);
    try {
      const cred = await confirmation.confirm(otp.trim());
      const idToken = await cred.user.getIdToken();
      // Stash the token for the password step by keeping the credential.
      window.__cargoloadPhoneIdToken = idToken;
      setPhoneStep("password");
    } catch (e) {
      setError(e?.message || "That code didn't match. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const setNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if ((newPassword || "").length < 6) { setError("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      const idToken = window.__cargoloadPhoneIdToken;
      const data = await resetPasswordByPhone(phone.trim(), idToken, newPassword);
      setPhoneStep("done");
      onDone(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message || "Could not update the password.");
    } finally {
      setBusy(false);
    }
  };

  const errorBlock = error && (
    <div
      data-testid="auth-error"
      className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md"
    >
      {error}
    </div>
  );

  if (phoneStep === "phone") {
    return (
      <form onSubmit={sendCode} className="space-y-4">
        <p className="text-sm text-slate-500">
          Enter the mobile number linked to your account (with country code). We'll SMS you a 6-digit code.
        </p>
        <div>
          <label htmlFor="auth-phone" className="overline block mb-1.5">Mobile number</label>
          <Input
            id="auth-phone"
            data-testid="auth-phone-input"
            type="tel"
            className="bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full"
            placeholder="+91 98765 43210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </div>
        {errorBlock}
        <Button
          type="submit"
          data-testid="auth-phone-send-btn"
          disabled={busy}
          className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50"
        >
          {busy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Sending OTP…</span> : "Send OTP →"}
        </Button>
      </form>
    );
  }
  if (phoneStep === "otp") {
    return (
      <form onSubmit={verifyOtp} className="space-y-4">
        <p className="text-sm text-slate-500">
          Enter the 6-digit code we sent to <span className="mono text-slate-900">{phone}</span>.
        </p>
        <div>
          <label htmlFor="auth-otp" className="overline block mb-1.5">One-time code</label>
          <Input
            id="auth-otp"
            data-testid="auth-otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-2xl tracking-widest text-center h-14 px-3 w-full"
            placeholder="123 456"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
            required
          />
        </div>
        {errorBlock}
        <Button
          type="submit"
          data-testid="auth-otp-verify-btn"
          disabled={busy || otp.length < 4}
          className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50"
        >
          {busy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</span> : "Verify code →"}
        </Button>
        <button
          type="button"
          onClick={() => { setPhoneStep("phone"); setOtp(""); setError(""); resetRecaptcha(); }}
          className="w-full mono text-xs uppercase tracking-widest text-slate-500 hover:text-[#2563EB]"
        >
          ← Change number
        </button>
      </form>
    );
  }
  if (phoneStep === "password") {
    return (
      <form onSubmit={setNewPasswordSubmit} className="space-y-4">
        <p className="text-sm text-slate-500">
          Pick a new password for your account.
        </p>
        <div>
          <label htmlFor="auth-newpw" className="overline block mb-1.5">New password</label>
          <Input
            id="auth-newpw"
            data-testid="auth-newpw-input"
            type="password"
            className="bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full"
            placeholder="At least 6 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={6}
            required
            autoFocus
          />
        </div>
        {errorBlock}
        <Button
          type="submit"
          data-testid="auth-newpw-submit-btn"
          disabled={busy || newPassword.length < 6}
          className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-bold mono uppercase tracking-widest text-xs h-11 transition-colors disabled:opacity-50"
        >
          {busy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Updating…</span> : "Set new password →"}
        </Button>
      </form>
    );
  }
  if (phoneStep === "done") {
    return (
      <div className="text-center py-6">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h3 className="heading text-lg font-bold mb-1">Password updated</h3>
        <p className="text-sm text-slate-500">Signing you in…</p>
        <Button
          type="button"
          onClick={onBackToLogin}
          className="mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-10 px-4"
        >
          Continue
        </Button>
      </div>
    );
  }
  return null;
}
